import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';
import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import {
  calculateQuantityFromHours,
  convertDateToMoment,
  dateIsAfter,
  resetToStartOfDay,
} from '../../../util/dates';
import { propTypes } from '../../../util/types';
import {
  AvailabilityDatesPicker,
  FieldNumberIncrementerInput,
  FieldSessionSelector,
  Form,
  IconSpinner,
  PrimaryButton,
  SecondaryButton,
} from '../../../components';
import EstimatedBreakdownMaybe, { estimatedTransaction } from './EstimatedBreakdownMaybe';
import { withViewport } from '../../../util/uiHelpers';
import { safeSumTwoIntegers } from '../../../util/currency';
import { checkIfDatesOverlap } from '../../../util/sessions';
import { getUserType } from '../../../util/listingHelpers';
import { USER_TYPE } from '../../../constants/userAttributes';
import { useSelector } from 'react-redux';
import VoucherInput from '../VoucherInput/VoucherInput';
import bus, { AuthEventTypes } from '../../../modules/bus';

import css from './BookingTimeForm.module.css';

const MOBILE_BREAKPOINT = 768;

const getMaxSeatsInTS = (timeSlots = []) =>
  timeSlots.reduce((maxSeats, slot) => Math.max(maxSeats, slot.availableSeats), 0);

const convertTimeStringToHours = timeString => {
  const [hoursString, minutesString] = timeString.split(':');

  const hours = Number.parseInt(hoursString, 10);
  const minutesAsHours = Number.parseInt(minutesString, 10) / 60;

  return hours + minutesAsHours;
};

const getHourDifferenceFromTimes = (startTime, endTime) => {
  return convertTimeStringToHours(endTime) - convertTimeStringToHours(startTime);
};

export const getSessionsForSelectedDates = (startDate, endDate, monthlyTimeSlots, timeZone, requestedSeats = 1, groupType = 'group') => {
  if (!monthlyTimeSlots?.timeSlots || !startDate || !endDate) return [];

  const start = resetToStartOfDay(startDate, timeZone);

  const end = resetToStartOfDay(endDate, timeZone, 1);

  const isGroupListing = groupType === 'group';

  const sessionsForDates = monthlyTimeSlots.timeSlots.reduce((sessions, slot) => {
    const { _id, price, startsAt, endsAt, startTime, endTime, isOvernight, status, availableSeats = 0 } = slot;

    // CRITICAL: Filter by available seats differently for group vs private experiences
    // GROUP: Check if session has enough seats for the requested number of people
    // PRIVATE: Only check if session is available (>0) - entire session booked by one party
    if (isGroupListing) {
      // Group listing: filter out sessions that don't have enough seats
      if (availableSeats < requestedSeats) {
        return sessions;
      }
    } else {
      // Private listing: only show sessions that are available (not fully booked)
      // Number of people doesn't affect which sessions are shown
      if (availableSeats === 0) {
        return sessions;
      }
    }

    const priceAmount = price?.amount || 0;

    const startDateInTz = convertDateToMoment(startsAt, timeZone);

    const isTSAfterBookingStart = dateIsAfter(startDateInTz, start);
    // For overnight sessions, `endsAt` is the checkout time (e.g., 10:00 on the last day).
    // The computed `end` boundary is midnight of that same day (e.g., Thursday 00:00),
    // so dateIsAfter(Thursday 00:00, Thursday 10:00) = FALSE, incorrectly filtering the session out.
    // Fix: for overnight, check that the session STARTS before `end` instead of checking endsAt.
    const endCheckMoment = isOvernight ? startDateInTz : convertDateToMoment(endsAt, timeZone);
    const isTSBeforeBookingEnd = dateIsAfter(end, endCheckMoment);

    if (isTSAfterBookingStart && isTSBeforeBookingEnd) {
      const newSession = {
        key: _id,
        value: {
          _id,
          sessionIds: [_id],
          price: priceAmount,
          startTime,
          endTime,
          startsAt,
          endsAt,
          isOvernight,
          status,
          availableSeats, // Include for UI display
        },
      };

      sessions.push(newSession);
    }

    return sessions;
  }, []);

  const highestPriceSession = sessionsForDates.reduce(
    (highest, session) => {
      const { startsAt, endsAt, startTime, endTime, price, isOvernight } = session.value;

      const hoursFromTimes = isOvernight
        ? calculateQuantityFromHours(startsAt, endsAt)
        : getHourDifferenceFromTimes(startTime, endTime);

      const hourlyPrice = price / hoursFromTimes;

      if (highest.hourlyPrice < hourlyPrice) {
        highest.sessionPrice = price;
        highest.hourlyPrice = hourlyPrice;
      }

      return highest;
    },
    { sessionPrice: 0, hourlyPrice: 0 }
  );

  return sessionsForDates.map(({ key, value }) => {
    const { startsAt, endsAt, startTime, endTime, price, isOvernight } = value;

    const hoursFromTimes = isOvernight
      ? calculateQuantityFromHours(startsAt, endsAt)
      : getHourDifferenceFromTimes(startTime, endTime);

    const hourlyPrice = price / hoursFromTimes;

    const valueWithSavings = { ...value };

    // If the current session costs the same as the highest one
    // we don't save anything on that session so we don't add 'savings'
    if (hourlyPrice !== highestPriceSession.hourlyPrice) {
      const savings = (1 - hourlyPrice / (highestPriceSession.hourlyPrice || 1)) * 100;

      valueWithSavings.savings = `-${Number.parseInt(savings, 10)}%`;
    }

    return { key, value: JSON.stringify(valueWithSavings) };
  });
};

const hasCompleteSelection = payload => {
  return payload.startDate && payload.endDate && payload.sessions.length > 0 && payload.seats;
};

const BookingTimeFormComponent = props => {
  const {
    intl,
    rootClassName,
    className,
    submitButtonWrapperClassName,
    price: unitPrice,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    voucherValidation,
    aggregatedWalletBalance,
    onFetchTransactionLineItems,
    listingId,
    isOwnListing,
    timeZone,
    endDatePlaceholder,
    startDatePlaceholder,
    unitType,
    viewport,
    onManageDisableScrolling,
    canShowChildren,
    groupType,
    canShowAdults,
    isMultiDay,
    multiDaySessionStartIndex,
    duration,
    onContactUser,
    sessionsDiscount,
    seasonalCharge,
    additionalPersonCharge,
    isFreeListing,
    onSubmit,
    listingAuthor,
    peopleAtMost,
    ...rest
  } = props;

  const { currentUser } = useSelector(state => state.user);
  const remainingWalletBalance = useSelector(state => state.ListingPage?.remainingWalletBalance);

  // Track previous sessions count to detect transitions (e.g. >0 -> 0)
  const prevSessionsCountRef = useRef(0);
  // Track last listing ID for which we fetched wallet balance
  const lastWalletFetchListingIdRef = useRef(null);

  const userHasWalletCredit = useMemo(() => {
    const privateBalances = currentUser?.attributes?.profile?.privateData?.walletBalances;
    const hasMultiCurrencyCredit =
      privateBalances && Object.values(privateBalances).some(balance => Number(balance) > 0);
    return hasMultiCurrencyCredit;
  }, [currentUser]);

  // Find the highest-value unused stored percentage discount (from partner QR).
  // Wallet credit takes priority, so only auto-apply stored discounts when the
  // user has no wallet balance available.
  const storedDiscountCode = useMemo(() => {
    if (userHasWalletCredit) return null;
    const usedVoucherCodes = currentUser?.attributes?.profile?.privateData?.usedVoucherCodes || [];
    const storedDiscounts = currentUser?.attributes?.profile?.privateData?.storedDiscounts || [];
    const unused = storedDiscounts
      .filter(d => !d.usedAt && !usedVoucherCodes.includes(d.code))
      .sort((a, b) => {
        const percentageDiff = (b.percentage || 0) - (a.percentage || 0);
        if (percentageDiff !== 0) {
          return percentageDiff;
        }

        return new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime();
    });
    return unused.length > 0 ? unused[0].code : null;
  }, [currentUser, userHasWalletCredit]);

  const buildBookingPayload = useCallback(
    (values = {}, extra = {}) => {
      const { dates = {}, sessions = [], adults = 1, children = 0, voucher } = values;
      const { startDate, endDate } = dates || {};
      const seats = safeSumTwoIntegers(adults, children);

      return {
        startDate,
        endDate,
        sessions,
        adults,
        children,
        seats,
        voucher: voucher || null,
        ...extra,
      };
    },
    []
  );

  const sendValidationFetch = useCallback(
    (payload, extraBookingData = {}) => {
      onFetchTransactionLineItems({
        bookingData: {
          voucher: payload.voucher,
          sessions: [],
          ...extraBookingData,
        },
        listingId,
        isOwnListing,
      });
    },
    [isOwnListing, listingId, onFetchTransactionLineItems]
  );

  const sendFullFetch = useCallback(
    payload => {
      onFetchTransactionLineItems({
        bookingData: payload,
        listingId,
        isOwnListing,
        seats: payload.seats,
      });
    },
    [isOwnListing, listingId, onFetchTransactionLineItems]
  );

  const triggerLineItemsFetch = useCallback(
    (values = {}, { mode = 'auto', extraBookingData = {} } = {}) => {
      // Safety check: Don't make API calls if listingId is missing (e.g., during navigation)
      if (!listingId) {
        return;
      }

      const payload = buildBookingPayload(values, extraBookingData);

      // walletOnly mode removed - wallet balance now computed from user.privateData.walletBalances

      if (mode === 'validation') {
        sendValidationFetch(payload, extraBookingData);
        return;
      }

      const selectionComplete = hasCompleteSelection(payload);

      if (mode === 'full' || (mode === 'auto' && selectionComplete)) {
        sendFullFetch(payload);
        return;
      }

      const shouldValidate = mode === 'auto' && (payload.voucher || aggregatedWalletBalance > 0);

      if (shouldValidate) {
        sendValidationFetch(payload, extraBookingData);
      }
    },
    [
      aggregatedWalletBalance,
      buildBookingPayload,
      isOwnListing,
      listingId,
      onFetchTransactionLineItems,
      sendFullFetch,
      sendValidationFetch,
    ]
  );

  // Fetch aggregated wallet balance on mount if user has wallet credit.
  // This allows displaying wallet credit even before sessions are selected
  // Runs on every navigation to a listing page (when listingId changes).
  // Skip the standalone wallet fetch if we already have a stored discount code,
  // because the auto-validation request for that code also returns wallet data.
  useEffect(() => {
    const listingIdUuid = listingId?.uuid;
    
    if (currentUser && userHasWalletCredit && listingIdUuid && !storedDiscountCode) {
      // Only fetch if we haven't fetched for this specific listing yet
      // This prevents redundant fetches on re-renders but allows fetches on navigation
      if (lastWalletFetchListingIdRef.current !== listingIdUuid) {
        lastWalletFetchListingIdRef.current = listingIdUuid;
        // Use validation mode with walletOnly flag to get aggregated balance
        triggerLineItemsFetch({}, { mode: 'validation', extraBookingData: { walletOnly: true } });
      }
    } else if (!listingIdUuid) {
      // Reset tracking when navigating away from listing
      lastWalletFetchListingIdRef.current = null;
    }
  }, [currentUser, triggerLineItemsFetch, userHasWalletCredit, listingId, storedDiscountCode]);

  const classes = classNames(rootClassName || css.root, className);

  const isMobileLayout = useMemo(() => viewport.width <= MOBILE_BREAKPOINT, [viewport.width]);

  const messages = useMemo(
    () => ({
      adults: intl.formatMessage({ id: 'General.adults' }),
      age: intl.formatMessage({ id: 'General.age18' }),
      children: intl.formatMessage({ id: 'General.children' }),
    }),
    [intl]
  );

  // Get the maximum possible seats that the user can pick from all of the timeslots
  // CRITICAL: Limit by peopleAtMost from listing data to prevent selecting more than allowed
  const maxPossibleSeats = useMemo(() => {
    const maxFromTimeSlots = getMaxSeatsInTS(monthlyTimeSlots?.timeSlots);
    // If peopleAtMost is defined, use it as the upper limit
    // Otherwise fall back to timeslot capacity (for backwards compatibility)
    return peopleAtMost ? Math.min(maxFromTimeSlots, peopleAtMost) : maxFromTimeSlots;
  }, [monthlyTimeSlots?.timeSlots, peopleAtMost]);

  const isListingAuthorBusiness = getUserType(listingAuthor) === USER_TYPE.BUSINESS;

  const resetDatesAndSessions = useCallback(formApi => {
    formApi.batch(() => {
      formApi.change('dates');
      formApi.change('sessions', []);
    });
    triggerLineItemsFetch(formApi?.getState()?.values || {}, { mode: 'validation' });
  }, [triggerLineItemsFetch]);

  // When seats (adults/children) are changed we want to refresh line items
  // AND validate that selected sessions still have enough available seats
  const handleSeatsChange = useCallback(formApi => {
    // Defer reading the form state to allow the field to update
    setTimeout(() => {
      const currentValues = formApi?.getState()?.values || {};
      const { adults = 1, children = 0, sessions = [] } = currentValues;
      const requestedSeats = safeSumTwoIntegers(adults, children);

      // CRITICAL: Validate selected sessions still have enough seats
      // Filter out sessions that no longer have sufficient available seats
      const validSessions = sessions.filter(sessionValue => {
        try {
          const session = typeof sessionValue === 'string' ? JSON.parse(sessionValue) : sessionValue;
          const sessionId = session._id;
          
          // Find this session in monthlyTimeSlots to check availableSeats
          const sessionData = monthlyTimeSlots?.timeSlots?.find(slot => slot._id === sessionId);
          
          if (!sessionData) {
            console.warn('[SEAT VALIDATION] Session not found in timeSlots:', sessionId);
            return false; // Remove session if we can't find its data
          }
          
          const hasEnoughSeats = (sessionData.availableSeats || 0) >= requestedSeats;
          
          if (!hasEnoughSeats) {
            console.log('[SEAT VALIDATION] Removing session - insufficient seats:', {
              sessionId,
              availableSeats: sessionData.availableSeats,
              requestedSeats,
            });
          }
          
          return hasEnoughSeats;
        } catch (e) {
          console.error('[SEAT VALIDATION] Error parsing session:', e);
          return false;
        }
      });

      // If some sessions were removed, update the form
      if (validSessions.length !== sessions.length) {
        formApi.change('sessions', validSessions);
        console.log('[SEAT VALIDATION] Removed invalid sessions:', {
          before: sessions.length,
          after: validSessions.length,
          requestedSeats,
        });
      }

      triggerLineItemsFetch(formApi?.getState()?.values || {});
    }, 0);
  }, [triggerLineItemsFetch, monthlyTimeSlots?.timeSlots]);

  const handleApplyVoucher = useCallback((voucherCode, formApi) => {
    // CRITICAL: Always clear and re-fetch when user clicks "Apply".
    // Empty or invalid codes fall back to wallet-only pricing.
    // Valid percentage codes are used only when no wallet credit is available.
    // FIXED codes are converted into wallet credit upstream.
    
    if (formApi) {
      // Update form field with voucher code (or empty to clear)
      formApi.change('voucher', voucherCode || null);
    }
    
    // Trigger fetch with the new voucher code (empty string clears it)
    triggerLineItemsFetch(formApi?.getState()?.values || {}, { mode: 'auto' });
  }, [triggerLineItemsFetch]);

  // When the values of the form are updated we need to fetch
  // lineItems from FTW backend for the EstimatedTransactionMaybe
  // In case you add more fields to the form, make sure you add
  // the values here to the bookingData object.
  const handleOnChange = useCallback(
    formValues => {
      const values = formValues.values || {};
      const sessionsCount = values.sessions?.length || 0;

      if (sessionsCount === 0 && prevSessionsCountRef.current > 0) {
        triggerLineItemsFetch(values, { mode: 'validation' });
      } else {
        triggerLineItemsFetch(values);
      }

      prevSessionsCountRef.current = sessionsCount;
    },
    [triggerLineItemsFetch]
  );

  const areListingSessionsOverlapping = useCallback(
    sessions => {
      if (isListingAuthorBusiness || !sessions || sessions.length === 1) return false;

      for (let i = 0; i < sessions.length; i++) {
        const { startsAt: startsAt1, endsAt: endsAt1 } = JSON.parse(sessions[i]);

        for (let j = i + 1; j < sessions.length; j++) {
          const { startsAt: startsAt2, endsAt: endsAt2 } = JSON.parse(sessions[j]);

          if (
            checkIfDatesOverlap(
              new Date(startsAt1),
              new Date(endsAt1),
              new Date(startsAt2),
              new Date(endsAt2)
            )
          )
            return true;
        }
      }

      return false;
    },
    [isListingAuthorBusiness]
  );

  const handleSubmit = useCallback(
    values => {
      const { sessions = [] } = values;

      const { startDate, endDate } = sessions.reduce((datesAcc, session) => {
        const { startsAt, endsAt } = JSON.parse(session);

        const sessionStart = convertDateToMoment(startsAt, timeZone);
        const sessionEnd = convertDateToMoment(endsAt, timeZone);

        datesAcc.startDate =
          !datesAcc.startDate || dateIsAfter(datesAcc.startDate, sessionStart)
            ? sessionStart
            : datesAcc.startDate;
        datesAcc.endDate =
          !datesAcc.endDate || dateIsAfter(sessionEnd, datesAcc.endDate)
            ? sessionEnd
            : datesAcc.endDate;

        return datesAcc;
      }, {});

      if (!startDate || !endDate) {
        return;
      }

      const payload = buildBookingPayload(values, {
        startDate: startDate.toDate(),
        endDate: endDate.toDate(),
        sessions,
      });

      onSubmit(payload);
    },
    [buildBookingPayload, onSubmit, timeZone]
  );

  if (!unitPrice) {
    return (
      <div className={classes}>
        <p className={css.error}>
          <FormattedMessage id="BookingTimeForm.listingPriceMissing" />
        </p>
      </div>
    );
  }

  return (
    <FinalForm
      {...rest}
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators }}
      render={fieldRenderProps => {
        const { handleSubmit, values, form } = fieldRenderProps;

        const { dates, children = 0, adults = 1, sessions = [] } = values;

        const { startDate, endDate } = dates || {};

        const hasSelectedSessions = sessions.length !== 0;

        const maxPossibleAdults = canShowChildren
          ? Math.max(maxPossibleSeats - children, 1)
          : maxPossibleSeats;
        const maxPossibleChildren = canShowAdults
          ? Math.max(maxPossibleSeats - adults, 0)
          : maxPossibleSeats;

        const selectedSeats = safeSumTwoIntegers(adults, children);

        const hasOverlappingSessions = areListingSessionsOverlapping(sessions);

        const submitDisabled =
          !startDate ||
          !endDate ||
          !hasSelectedSessions ||
          !selectedSeats ||
          hasOverlappingSessions;

        // This is the place to collect breakdown estimation data. See the
        // EstimatedBreakdownMaybe component to change the calculations
        // for customized payment processes.
        const bookingData =
          startDate && endDate && hasSelectedSessions
            ? {
                unitType,
                startDate,
                endDate,
                timeZone,
              }
            : null;

        const showEstimatedBreakdown =
          bookingData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;

        const estimatedTx = showEstimatedBreakdown
          ? estimatedTransaction(
              startDate,
              endDate,
              lineItems,
              'customer',
              currentUser,
              listingAuthor
            )
          : null;

        const bookingInfoMaybe = showEstimatedBreakdown ? (
          <div className={css.priceBreakdownContainer}>
            <EstimatedBreakdownMaybe
              bookingData={bookingData}
              lineItems={lineItems}
              sessionsDiscount={sessionsDiscount}
              seasonalCharge={seasonalCharge}
              additionalPersonCharge={additionalPersonCharge}
              currentUser={currentUser}
              author={listingAuthor}
              remainingWalletBalance={remainingWalletBalance}
              currency={unitPrice?.currency}
            />
          </div>
        ) : null;

        const loadingSpinnerMaybe = fetchLineItemsInProgress ? (
          <IconSpinner className={css.spinner} />
        ) : null;

        const bookingInfoErrorMaybe = fetchLineItemsError ? (
          <span className={css.sideBarError}>
            <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
          </span>
        ) : null;

        const overlappingSessionsError = hasOverlappingSessions ? (
          <span className={css.sideBarError}>
            <FormattedMessage id="BookingDatesForm.overlappingSessionsError" />
          </span>
        ) : null;

        const submitButtonClasses = classNames(
          submitButtonWrapperClassName || css.submitButtonWrapper
        );

        // CRITICAL: Pass selectedSeats to filter sessions by available capacity
        // This ensures only sessions with enough seats are shown in the UI
        // For private experiences, groupType determines if seat count affects availability
        const sessionsForSelectedDates = getSessionsForSelectedDates(
          startDate,
          endDate,
          monthlyTimeSlots,
          timeZone,
          selectedSeats,
          groupType
        );

        const availability = sessionsForSelectedDates.reduce((currentStatus, el) => {
          try {
            const { status } = JSON.parse(el.value);
            return currentStatus && status !== currentStatus ? 'PARTIALLY_AVAILABLE' : status;
          } catch (e) {
            return null;
          }
        }, null);

        return (
          <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
            <FormSpy subscription={{ values: true }} onChange={handleOnChange} />

            <div className={css.seats}>
              {canShowAdults && (
                <FieldNumberIncrementerInput
                  id="adults"
                  name="adults"
                  label={messages.adults}
                  description={messages.age}
                  fieldClassName={css.seatField}
                  min="1"
                  max={maxPossibleAdults}
                  onChange={() => handleSeatsChange(form)}
                />
              )}
              {canShowChildren && (
                <FieldNumberIncrementerInput
                  id="children"
                  name="children"
                  label={messages.children}
                  fieldClassName={css.seatField}
                  min={canShowAdults ? "0" : "1"}
                  max={maxPossibleChildren}
                  onChange={() => handleSeatsChange(form)}
                />
              )}
            </div>
            {timeZone ? (
              <AvailabilityDatesPicker
                intl={intl}
                selectedDates={dates}
                isMobileLayout={isMobileLayout}
                onManageDisableScrolling={onManageDisableScrolling}
                toggleClassName={css.datesToggle}
                timeZone={timeZone}
                monthlyTimeSlots={monthlyTimeSlots}
                selectedSeats={selectedSeats}
                isMultiDay={isMultiDay}
                multiDaySessionStartIndex={multiDaySessionStartIndex}
                duration={duration}
                onDatesChanged={({ startDate, endDate, pickerComplete } = {}) => {
                  form.change('sessions', []);
                  triggerLineItemsFetch(form.getState()?.values || {}, { mode: 'validation' });
                  // Auto-open the session selector only when the date picker is fully done
                  // (both dates confirmed and the picker popup is closing).
                  // pickerComplete=false when only the start date was clicked (non-multiday),
                  // so we don't open prematurely.
                  if (startDate && endDate && pickerComplete) {
                    setTimeout(() => {
                      bus.broadcastEvent(AuthEventTypes.FILTER_POPUP_TOGGLE, 'BookingTimeFormSessionPopup');
                    }, 50);
                  }
                }}
                useAvailability
                availability={availability}
                placeholder={intl.formatMessage({ id: 'BookingTimeForm.selectAvailableDays' })}
              />
            ) : null}

            <FieldSessionSelector
              id="sessions"
              name="sessions"
              options={sessionsForSelectedDates}
              intl={intl}
              startDate={startDate}
              endDate={endDate}
              timeZone={timeZone}
              isMultiDay={isMultiDay}
              sessionsDiscount={sessionsDiscount}
              duration={duration}
              estimatedTx={estimatedTx}
              currency={unitPrice.currency}
              peopleAtMost={peopleAtMost}
              groupType={groupType}
              clearSessions={() => {
                form.change('sessions', []);
                triggerLineItemsFetch(form.getState()?.values || {}, { mode: 'validation' });
              }}
              listingAuthor={listingAuthor}
            />

            {/* Voucher Input Component */}
            <VoucherInput
              intl={intl}
              onApplyVoucher={(code) => handleApplyVoucher(code, form)}
              fetchLineItemsError={fetchLineItemsError}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              voucherValidation={voucherValidation}
              currency={unitPrice.currency}
              storedDiscountCode={storedDiscountCode}
            />

            {bookingInfoMaybe}
            {loadingSpinnerMaybe}
            {bookingInfoErrorMaybe}
            {overlappingSessionsError}

            <div className={submitButtonClasses}>
              <PrimaryButton type="submit" disabled={submitDisabled}>
                <FormattedMessage id="General.checkout" />
              </PrimaryButton>

              {!!onContactUser && (
                <>
                  <p className={css.or}>
                    <FormattedMessage id="General.or" />
                  </p>
                  <SecondaryButton type="button" color="black" onClick={onContactUser}>
                    <FormattedMessage id="General.enquiry" />
                  </SecondaryButton>
                </>
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

BookingTimeFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  price: null,
  isOwnListing: false,
  listingId: null,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  monthlyTimeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
  walletBalance: null,
  peopleAtMost: null,
  groupType: 'group', // Default to group for backwards compatibility
};

BookingTimeFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  unitType: propTypes.bookingUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  listingId: propTypes.uuid,
  monthlyTimeSlots: object,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  onContactUser: func,

  walletBalance: number,
  peopleAtMost: number,
  groupType: string,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,
};

const BookingTimeForm = compose(
  withViewport,
  injectIntl
)(BookingTimeFormComponent);
BookingTimeForm.displayName = 'BookingTimeForm';

export default BookingTimeForm;
